/**
 * main.js —— 装配
 *
 * 摄像头不是必需的：没授权、没设备、MediaPipe 加载失败，
 * 都自动退回键盘操作（F 摇卦 / P 变卦 / R 重来），功能完全一致。
 * 一个算卦程序不该因为没摄像头就用不了。
 */

import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';

import { Field } from './field.js';
import {
  cellMapFromMeihua, Director, isAimFollowState, isDepthOrientationState, STATE
} from './states.js';
import {
  classify, fingerRatios, fingertipAimPosition, isGestureControlBlocked,
  palmOrientation, palmRotationTarget,
  GestureDebouncer, Smoother
} from './gestures.js';
import { HandTracker } from './hands.js';
import { seededRandom } from '../src/cast.js';
import {
  AIM, APP_UI, COLORS, CAMERA, BLOOM, FIELD, PARTICLES, STARS, QUALITY, GESTURE,
  DEBUG_UI, PERFORMANCE, TIMING, TIMING_TOOLTIP, TOUCH, VIEWPORT_FILL
} from './tuning.js';
import { parseSceneOptions } from './url-options.js';
import { LiuyaoAudio } from './audio.js';
import { toPrompt } from '../src/index.js';
import { USE_GOD_MAP } from '../src/analyze.js';
import { castByNumbers, castByTime } from '../src/meihua.js';
import { flyingStarRingModel } from './feixing-view.js';
import { buildReportView, REPORT_DEBUG_AUDIT } from '../src/report-view.js';
import { PLAIN_VERDICT_NOTE } from '../src/plain-language.js';

const sceneOptions = parseSceneOptions(location.search);
const debugEnabled = sceneOptions.debug;
const cameraDebugEnabled = sceneOptions.cameraDebug;
const measurementMode = sceneOptions.measure;
const mobile = QUALITY.isMobile || (measurementMode && sceneOptions.mobilePreview);
const measurementSeed = sceneOptions.measurementSeed;
const sceneBackground = sceneOptions.kiosk ? COLORS.kioskBackground : COLORS.background;
document.documentElement.style.setProperty('--mode-top', `${APP_UI.modeTop}px`);
document.documentElement.style.setProperty('--mode-left', `${APP_UI.modeLeft}px`);
document.documentElement.style.setProperty('--mode-gap', `${APP_UI.modeGap}px`);
document.documentElement.style.setProperty('--mode-panel-width', `${APP_UI.modePanelWidth}px`);
document.documentElement.style.setProperty(
  '--timing-tooltip-max-width',
  `${TIMING_TOOLTIP.maxWidth}px`
);
if (measurementMode) document.documentElement.classList.add('measure-mode');
if (sceneOptions.kiosk) document.documentElement.classList.add('kiosk-mode');
if (cameraDebugEnabled) document.documentElement.classList.add('camera-debug-mode');
document.documentElement.style.setProperty(
  '--void',
  new THREE.Color(sceneBackground).getStyle(THREE.SRGBColorSpace)
);
if (debugEnabled) {
  document.documentElement.classList.add('debug-mode');
  document.documentElement.style.setProperty('--debug-preview-width', `${DEBUG_UI.previewWidth}px`);
  document.documentElement.style.setProperty('--debug-preview-aspect', String(DEBUG_UI.previewAspect));
  document.documentElement.style.setProperty('--debug-preview-inset', `${DEBUG_UI.previewInset}px`);
}

/* ------------------------------------------------------------ 渲染 */

const canvas = document.getElementById('stage');
const renderer = new THREE.WebGLRenderer({ canvas, antialias: !mobile, alpha: false });
renderer.setPixelRatio(measurementMode
  ? QUALITY.measurementPixelRatio
  : Math.min(devicePixelRatio, mobile ? 2 : 2.5));
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.NoToneMapping;
// EffectComposer 的离屏 clear 会先按 renderer.outputColorSpace 编码一次，最终拷回
// 屏幕时 WebGLRenderer 又编码一次。这里预先再线性化，抵消背景独有的重复转换；
// 材质与 CanvasTexture 仍按标准 sRGB → linear → sRGB 路径渲染。
const composerClearColor = new THREE.Color(sceneBackground).convertSRGBToLinear();
renderer.setClearColor(composerClearColor, 1);

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(CAMERA.fov, 1, CAMERA.near, CAMERA.far);
camera.position.set(0, 0, CAMERA.distance);

const field = new Field({
  textureSize: mobile ? FIELD.textureSizeMobile : FIELD.textureSize,
  particleCount: mobile ? PARTICLES.countMobile : PARTICLES.count,
  starCount: mobile ? STARS.countMobile : STARS.count
});
scene.add(field.root);

const composer = new EffectComposer(renderer);
composer.addPass(new RenderPass(scene, camera));
const useBloom = !mobile || QUALITY.bloomOnMobile;
let bloomPass = null;
if (useBloom) {
  bloomPass = new UnrealBloomPass(
    new THREE.Vector2(1, 1), BLOOM.strength, BLOOM.radius, BLOOM.threshold
  );
  composer.addPass(bloomPass);
}
composer.addPass(new OutputPass());

let cameraBaseDistance = CAMERA.distance;

function updateCameraFraming() {
  // 立体态会把最外环沿锥面推远 FIELD.cone * FIELD.outerRadius。
  // 相机同步前移同样的距离，只补偿构图，不改十三层的世界几何和锥深公式。
  const depthPull = field.control.depth
    * FIELD.cone
    * FIELD.outerRadius
    * CAMERA.depthFramingCompensation;
  camera.position.z = Math.max(CAMERA.near * 4, cameraBaseDistance - depthPull);
}

function resize() {
  const bounds = canvas.getBoundingClientRect();
  const w = Math.max(1, Math.round(bounds.width || innerWidth));
  const h = Math.max(1, Math.round(bounds.height || innerHeight));
  // CSS 决定竖屏上方 60% 的阵盘区域；只更新 drawing buffer，
  // 不让 Three.js 写入内联 width/height 反过来覆盖响应式布局。
  renderer.setSize(w, h, false);
  composer.setSize(w, h);
  camera.aspect = w / h;
  const displayDistance = CAMERA.distance * (
    sceneOptions.fullDisplay ? VIEWPORT_FILL.standard / VIEWPORT_FILL.full : 1
  );
  const compactLandscape = innerWidth > innerHeight && innerHeight <= 600;
  cameraBaseDistance = Math.max(
    displayDistance * (compactLandscape ? CAMERA.compactLandscapeDistanceScale : 1),
    displayDistance * CAMERA.portraitDistanceScale / camera.aspect
  );
  updateCameraFraming();
  camera.updateProjectionMatrix();
  const rect = renderer.domElement.getBoundingClientRect();
  if (
    Math.abs(rect.width - w) > QUALITY.resizeTolerancePx ||
    Math.abs(rect.height - h) > QUALITY.resizeTolerancePx
  ) {
    console.warn('[scene:resize-mismatch]', {
      expected: { width: w, height: h },
      actual: { width: rect.width, height: rect.height }
    });
  }
}
addEventListener('resize', resize);
globalThis.visualViewport?.addEventListener('resize', resize, { passive: true });
resize();

function logSceneDiagnostics() {
  const rect = renderer.domElement.getBoundingClientRect();
  const taijiWorld = field.taiji.getWorldPosition(new THREE.Vector3()).toArray();
  const centerGlowWorld = field.centerGlow.getWorldPosition(new THREE.Vector3()).toArray();
  console.info('[scene:diagnostics]', JSON.stringify({
    rendererWidth: renderer.domElement.width,
    rendererHeight: renderer.domElement.height,
    rect: {
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
      top: rect.top,
      right: rect.right,
      bottom: rect.bottom,
      left: rect.left
    },
    innerWidth,
    innerHeight,
    cameraAspect: camera.aspect,
    devicePixelRatio,
    rendererPixelRatio: renderer.getPixelRatio(),
    rendererOutputColorSpace: renderer.outputColorSpace,
    rendererToneMapping: renderer.toneMapping,
    rendererToneMappingExposure: renderer.toneMappingExposure,
    colorManagementEnabled: THREE.ColorManagement.enabled,
    composerWidth: composer.readBuffer.width,
    composerHeight: composer.readBuffer.height,
    composerReadColorSpace: composer.readBuffer.texture.colorSpace,
    composerWriteColorSpace: composer.writeBuffer.texture.colorSpace,
    rootPosition: field.root.position.toArray(),
    rootRotation: [field.root.rotation.x, field.root.rotation.y, field.root.rotation.z],
    taijiWorld,
    centerGlowWorld
  }));
}

/* ------------------------------------------------------------ HUD */

const hud = {
  title: document.getElementById('hudTitle'),
  sub: document.getElementById('hudSub'),
  calibration: document.getElementById('calibrationStatus'),
  lines: document.getElementById('hudLines'),
  panel: document.getElementById('panel'),
  cam: document.getElementById('camState'),
  cameraNotice: document.getElementById('cameraLoadingNotice')
};
const gestureDebug = {
  panel: document.getElementById('gestureDebug'),
  ratios: document.getElementById('debugRatios'),
  classify: document.getElementById('debugClassify'),
  debounce: document.getElementById('debugDebounce'),
  palmLandmarks: document.getElementById('debugPalmLandmarks'),
  palmRaw: document.getElementById('debugPalmRaw'),
  palmSmooth: document.getElementById('debugPalmSmooth'),
  rootRotation: document.getElementById('debugRootRotation'),
  verdictSwitch: document.getElementById('debugVerdictSwitch'),
  record: document.getElementById('debugRecord'),
  recordStatus: document.getElementById('debugRecordStatus'),
  preview: document.getElementById('cameraPreview'),
  video: document.getElementById('cam'),
  overlay: document.getElementById('handOverlay')
};
function createCameraDiagnostics() {
  // The early inline diagnostic bootstrap owns this DOM so import/top-level
  // failures remain visible even if this module does not finish evaluating.
  const root = document.getElementById('cameraDiagnostics');
  if (!root) return null;
  return {
    root,
    video: root.querySelector('#cameraDiagVideo'),
    fps: root.querySelector('#cameraDiagFps'),
    hands: root.querySelector('#cameraDiagHands'),
    runtime: root.querySelector('#cameraDiagRuntime'),
    phase: root.querySelector('#cameraDiagPhase'),
    audio: root.querySelector('#cameraDiagAudio'),
    audioPulse: root.querySelector('#cameraDiagAudioPulse'),
    globalError: root.querySelector('#cameraDiagGlobalError'),
    cameraError: root.querySelector('#cameraDiagCameraError')
  };
}
// Production creates no diagnostic DOM and does no per-frame metric painting.
const cameraDiagnostics = cameraDebugEnabled ? createCameraDiagnostics() : null;
const modeUi = {
  dock: document.getElementById('modeDock'),
  buttons: [...document.querySelectorAll('[data-mode]')],
  meihuaControls: document.getElementById('meihuaControls'),
  castTime: document.getElementById('castMeihuaTime'),
  castNumbers: document.getElementById('castMeihuaNumbers'),
  numberA: document.getElementById('meihuaNumberA'),
  numberB: document.getElementById('meihuaNumberB'),
  category: document.getElementById('categorySelect'),
  question: document.getElementById('questionInput'),
  timeScope: document.getElementById('timeScopeSelect')
};
const flyingStarUi = {
  buttons: [...document.querySelectorAll('[data-feixing-scale]')],
  center: document.getElementById('flyingStarCenter')
};
const timingTooltip = {
  root: document.getElementById('timingTooltip'),
  title: document.querySelector('#timingTooltip strong'),
  body: document.querySelector('#timingTooltip span')
};
const soundButton = document.getElementById('toggleSound');
const soundUnlockHint = document.getElementById('soundUnlockHint');
const mobileMenuButton = document.getElementById('mobileMenuToggle');
const qualityBadge = document.getElementById('qualityBadge');
const shakeButton = document.getElementById('enableShake');
const manualCastButton = document.getElementById('manualCast');
const audio = new LiuyaoAudio({ forceMuted: sceneOptions.mute });
void audio.preload();
// Chrome Kiosk 由启动脚本显式放开本地页自动播放；普通浏览器若仍拦截，
// 后面的首个 pointerdown / touchstart / keydown 会从同一处解锁。
if (sceneOptions.kiosk) void audio.resume().then(() => syncSoundUi());

function syncSoundUi() {
  const status = audio.status;
  document.documentElement.dataset.sound = status;
  soundButton.setAttribute('aria-pressed', String(!audio.muted));
  soundButton.textContent = status === 'muted'
    ? '音效：关'
    : (status === 'ready' ? '音效：开' : '音效：待启');
  soundUnlockHint.hidden = status !== 'locked';
}

async function unlockSound({ enable = false } = {}) {
  if (enable && !sceneOptions.mute) audio.setMuted(false);
  const resumePromise = audio.resume();
  watchAudioContextState();
  const ready = await resumePromise;
  syncSoundUi();
  return ready;
}

soundButton.onclick = async () => {
  if (audio.status === 'locked') {
    audio.setMuted(false);
    audio.unlockFromGesture({ audible: true });
    watchAudioContextState();
    if (await unlockSound()) removeFirstSoundGestureListeners();
  } else {
    audio.toggleMuted();
    if (!audio.muted) {
      if (await unlockSound()) removeFirstSoundGestureListeners();
    }
  }
  syncSoundUi();
};

const firstSoundGestureOptions = { capture: true, passive: true };
let firstSoundUnlockPending = false;
let firstSoundUnlockTrigger = null;
let firstSoundGestureListenersInstalled = false;
let watchedAudioContext = null;
function installFirstSoundGestureListeners() {
  if (firstSoundGestureListenersInstalled) return;
  document.addEventListener('pointerdown', handleFirstSoundGesture, firstSoundGestureOptions);
  document.addEventListener('touchstart', handleFirstSoundGesture, firstSoundGestureOptions);
  firstSoundGestureListenersInstalled = true;
}
function removeFirstSoundGestureListeners() {
  document.removeEventListener('pointerdown', handleFirstSoundGesture, firstSoundGestureOptions);
  document.removeEventListener('touchstart', handleFirstSoundGesture, firstSoundGestureOptions);
  firstSoundGestureListenersInstalled = false;
}
function watchAudioContextState() {
  const context = audio.context;
  if (!context || context === watchedAudioContext) return;
  watchedAudioContext = context;
  context.addEventListener?.('statechange', () => {
    syncSoundUi();
    // iOS 锁屏、切后台或音频设备变更后会再次挂起 context。
    // 重新挂回一次性手势监听，下一次触摸便能同步恢复。
    if (context.state !== 'running' && !audio.muted) installFirstSoundGestureListeners();
  });
}
async function handleFirstSoundGesture(event) {
  // The button's click handler owns its on/off semantics. Every other first
  // touch enables sound before the clicked control handles its action.
  const soundButtonGesture = event.target === soundButton || soundButton.contains(event.target);
  if (!soundButtonGesture && !sceneOptions.mute) audio.setMuted(false);
  audio.unlockFromGesture({ audible: !soundButtonGesture && !sceneOptions.mute });
  watchAudioContextState();
  firstSoundUnlockTrigger ??= event.type;
  syncSoundUi();
  if (soundButtonGesture) return;
  if (firstSoundUnlockPending) return;
  firstSoundUnlockPending = true;
  try {
    if (await unlockSound({ enable: true })) removeFirstSoundGestureListeners();
  } finally {
    firstSoundUnlockPending = false;
  }
}
installFirstSoundGestureListeners();
document.addEventListener('keydown', async (event) => {
  if (!sceneOptions.mute) audio.setMuted(false);
  audio.unlockFromGesture({ audible: !sceneOptions.mute });
  watchAudioContextState();
  firstSoundUnlockTrigger ??= event.type;
  if (await unlockSound({ enable: true })) removeFirstSoundGestureListeners();
}, { capture: true, once: true });
addEventListener('pageshow', () => {
  if (audio.context && audio.context.state !== 'running' && !audio.muted) {
    installFirstSoundGestureListeners();
  }
});
document.addEventListener('visibilitychange', () => {
  if (!document.hidden && audio.context && audio.context.state !== 'running' && !audio.muted) {
    installFirstSoundGestureListeners();
  }
});
syncSoundUi();
gestureDebug.panel.hidden = !debugEnabled;
gestureDebug.preview.hidden = !debugEnabled;

const HINTS = {
  [STATE.DORMANT]: ['伸出食指 · 唤醒阵眼', 'POINT · AWAKEN THE CENTER'],
  [STATE.AIM]: ['张开手掌 · 展开罗盘', 'OPEN YOUR PALM · EXPAND THE FIELD'],
  [STATE.EXPANDING]: ['罗盘展开中', 'FIELD EXPANDING'],
  [STATE.CASTING]: ['握拳六次 · 摇卦成爻', 'CLENCH SIX TIMES · CAST THE LINES'],
  [STATE.FORMED]: ['张开手掌 · 引动变卦', 'OPEN YOUR PALM · REVEAL THE CHANGE'],
  [STATE.TRANSFORM]: ['握拳 · 收卦', 'CLENCH · CLOSE THE FIELD'],
  [STATE.COLLAPSE]: ['', '']
};

const VALUE_LABEL = { 6: '老阴', 7: '少阳', 8: '少阴', 9: '老阳' };

function setHint(state) {
  const [zh, en] = HINTS[state] || ['', ''];
  hud.title.textContent = zh;
  hud.sub.textContent = en;
}

function renderLines() {
  hud.lines.innerHTML = director.values
    .map((v, i) => {
      const moving = v === 6 || v === 9;
      const bar = v === 7 || v === 9 ? '<i class="yang"></i>' : '<i class="yin"></i><i class="yin"></i>';
      return `<div class="ln ${moving ? 'moving' : ''}">
        <b>${i + 1}</b><span class="bars">${bar}</span><em>${VALUE_LABEL[v]}</em>
      </div>`;
    })
    .reverse()
    .join('');
}

function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

const TIME_SCOPE_LABELS = Object.freeze({
  none: '不问时间', near: '近期（30 天）', mid: '半年内', long: '长期'
});

function questionMarkup(
  question,
  category = sceneOptions.category,
  timeScope = sceneOptions.timeScope
) {
  if (!question) return '';
  const categoryText = category || '未选择类别（以世爻为代表）';
  const timeText = TIME_SCOPE_LABELS[timeScope] || TIME_SCOPE_LABELS.none;
  return `<div class="hexmeta">所问 · ${escapeHtml(categoryText)} · ${escapeHtml(timeText)}</div>
    <div class="facts" data-question>${escapeHtml(question)}</div>`;
}

function renderPendingQuestion(
  question = sceneOptions.question,
  category = sceneOptions.category,
  timeScope = sceneOptions.timeScope
) {
  if (!question) {
    hud.panel.hidden = true;
    return;
  }
  hud.panel.classList.remove('result-panel');
  hud.panel.hidden = false;
  hud.panel.innerHTML = `${questionMarkup(question, category, timeScope)}
    <div class="hexmeta">等待起卦</div>`;
}

function reportDebugMarkup(report, timing) {
  const sections = REPORT_DEBUG_AUDIT.map(({ key, status, note }) => `
    <details class="report-debug-field" open>
      <summary><code>${escapeHtml(key)}</code><b>${escapeHtml(status)}</b></summary>
      <p>${escapeHtml(note)}</p>
      <pre>${escapeHtml(JSON.stringify(report[key], null, 2))}</pre>
    </details>
  `).join('');
  const unresolved = timing?.unresolved || [];
  return `
    <div class="report-debug-banner">
      仅供内部检查 · 原始字段不代表可直接出售的完整报告
    </div>
    ${sections}
    <details class="report-debug-field report-debug-gap" open>
      <summary><code>timing.unresolved</code><b>报告模型尚未接入</b></summary>
      <p>规则已命中但暂时无法生成日期的项目；当前不在 report 的六个字段中。</p>
      <pre>${escapeHtml(JSON.stringify(unresolved, null, 2))}</pre>
    </details>
  `;
}

function evidenceListMarkup(items, emptyText = '本卦未列出此类事实。') {
  const rows = (items || []).map((item) => `<li>${escapeHtml(item)}</li>`).join('');
  return rows ? `<ul>${rows}</ul>` : `<p class="report-evidence-empty">${escapeHtml(emptyText)}</p>`;
}

function reportEvidenceMarkup(draft) {
  const relationGroup = (title, items, emptyText) => `
    <section class="report-evidence-subgroup">
      <h4>${escapeHtml(title)}</h4>
      ${evidenceListMarkup(items, emptyText)}
    </section>`;
  const timingGroups = draft.timing.groups.map((group) => `
    <details class="report-evidence-subgroup report-timing-group" ${group.scale === 'day' ? 'open' : ''}>
      <summary>${escapeHtml(group.label)} · ${group.items.length} 个</summary>
      <p class="report-evidence-note">${escapeHtml(group.hint)}</p>
      <div class="report-timing-list">
        ${group.items.map((item) => `
          <article class="report-timing-item">
            <strong>${escapeHtml(item.date)} · ${escapeHtml(item.ganzhi)}</strong>
            <span>${escapeHtml(item.reasons.join('、'))}</span>
            ${item.sourceRefs.length
              ? `<small>出处 ${item.sourceRefs.map((number) => `[${number}]`).join(' ')}</small>`
              : ''}
          </article>`).join('')}
      </div>
    </details>`).join('');
  const unresolved = draft.timing.unresolved.length
    ? `<details class="report-evidence-pending">
        <summary>另有 ${draft.timing.unresolved.length} 条规则暂时无法换算日期</summary>
        ${evidenceListMarkup(draft.timing.unresolved.map(({ ruleId, reason }) => `${ruleId}：${reason}`))}
      </details>`
    : '';
  const sourceItems = draft.sources.items.map((source) => (
    `[${source.number}] ${source.book}${source.chapter ? `·${source.chapter}` : ''}`
      + `${source.note ? `：${source.note.replace(/zsby-\d+/g, '古例')}` : ''}`
  ));
  return `
    <div class="report-debug-banner">内部整理版 · ${escapeHtml(draft.note)}</div>
    <article class="report-evidence-block">
      <h3>1 · ${escapeHtml(draft.useGod.title)}</h3>
      <p class="report-evidence-lead">${escapeHtml(draft.useGod.anchor)}</p>
      ${draft.useGod.selection ? `<p>${escapeHtml(draft.useGod.selection)}</p>` : ''}
      ${evidenceListMarkup(draft.useGod.facts)}
    </article>
    <article class="report-evidence-block">
      <h3>2 · ${escapeHtml(draft.relations.title)}</h3>
      <p class="report-evidence-note">${escapeHtml(draft.relations.note)}</p>
      ${relationGroup('与用神直接相连', draft.relations.direct, '未见已发动或暗动的爻与用神直接相连。')}
      ${relationGroup('静态生克关系', draft.relations.static, '未见其他静爻对用神形成五行生克。')}
      ${relationGroup('其他动爻关系', draft.relations.other, '未见其他动爻关系。')}
      ${relationGroup('卦体与合局结构', draft.relations.structural, '未见额外卦体或合局结构。')}
      ${draft.relations.pending ? `<p class="report-evidence-pending">${escapeHtml(draft.relations.pending)}</p>` : ''}
    </article>
    <article class="report-evidence-block">
      <h3>3 · ${escapeHtml(draft.timing.title)}</h3>
      <p class="report-evidence-note">${escapeHtml(draft.timing.note)}</p>
      <p class="report-evidence-note">${escapeHtml(draft.timing.orderNote)}</p>
      ${timingGroups || '<p class="report-evidence-empty">当前没有可换算的时间候选。</p>'}
      ${unresolved}
    </article>
    <article class="report-evidence-block">
      <h3>4 · ${escapeHtml(draft.sources.title)}</h3>
      <p class="report-evidence-note">${escapeHtml(draft.sources.note)}</p>
      ${evidenceListMarkup(sourceItems, '当前没有已接入的出处。')}
    </article>
  `;
}

function renderResult(result) {
  const p = result.primary;
  const viewModel = buildReportView(result, director.timing);
  const { technical, plain, preview, report, evidenceDraft } = viewModel;
  const showFullReport = !sceneOptions.reportLocked || sceneOptions.reportDebug;
  if (sceneOptions.reportDebug) {
    globalThis.__reportDebug = { result, timing: director.timing, viewModel };
  }
  const panelSnapshot = () => {
    const rect = hud.panel.getBoundingClientRect();
    const style = getComputedStyle(hud.panel);
    const active = document.activeElement;
    const content = document.getElementById('verdictContent');
    const visiblePane = content?.querySelector('[data-verdict-pane]:not([hidden])');
    return {
      windowScrollY: window.scrollY,
      documentScrollTop: document.documentElement.scrollTop,
      bodyScrollTop: document.body.scrollTop,
      rect: {
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height,
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
        left: rect.left
      },
      style: {
        position: style.position,
        top: style.top,
        transform: style.transform
      },
      panelScrollTop: hud.panel.scrollTop,
      panelScrollHeight: hud.panel.scrollHeight,
      contentScrollTop: content?.scrollTop ?? null,
      contentScrollHeight: content?.scrollHeight ?? null,
      visiblePane: visiblePane?.dataset.verdictPane ?? null,
      activeElement: active
        ? { tagName: active.tagName, id: active.id, text: active.textContent?.trim().slice(0, 24) || '' }
        : null
    };
  };
  const reportVerdictSwitch = (event, view, before) => {
    if (!debugEnabled) return;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const payload = {
        event: {
          type: event.type,
          targetTagName: event.target?.tagName || '',
          targetId: event.target?.id || ''
        },
        view,
        before,
        after: panelSnapshot()
      };
      console.info('[verdict:switch]', JSON.stringify(payload));
      if (debugEnabled && gestureDebug.verdictSwitch) {
        const b = payload.before;
        const a = payload.after;
        const activeLabel = (item) => `${item?.tagName || '—'}#${item?.id || '—'}`;
        gestureDebug.verdictSwitch.textContent = [
          `verdict ${payload.event.type} ${payload.event.targetTagName}#${payload.event.targetId} → ${view}`,
          `page ${b.windowScrollY}/${b.documentScrollTop}/${b.bodyScrollTop} → ${a.windowScrollY}/${a.documentScrollTop}/${a.bodyScrollTop}`,
          `rect y ${b.rect.y.toFixed(2)} h ${b.rect.height.toFixed(2)} → y ${a.rect.y.toFixed(2)} h ${a.rect.height.toFixed(2)}`,
          `style ${b.style.position} top ${b.style.top} transform ${b.style.transform} → ${a.style.position} top ${a.style.top} transform ${a.style.transform}`,
          `content ${b.visiblePane || '—'} h ${b.contentScrollHeight ?? '—'} → ${a.visiblePane || '—'} h ${a.contentScrollHeight ?? '—'}`,
          `active ${activeLabel(b.activeElement)} → ${activeLabel(a.activeElement)}`
        ].join(' | ');
        gestureDebug.verdictSwitch.dataset.log = JSON.stringify(payload);
      }
    }));
  };
  const renderVerdictView = (view) => {
    const container = document.getElementById('verdictContent');
    if (!container) return;
    for (const pane of container.querySelectorAll('[data-verdict-pane]')) {
      pane.hidden = pane.dataset.verdictPane !== view;
    }
    for (const button of hud.panel.querySelectorAll('[data-verdict-view]')) {
      button.setAttribute('aria-pressed', String(button.dataset.verdictView === view));
    }
    container.scrollTop = 0;
  };
  hud.panel.classList.add('result-panel');
  hud.panel.classList.toggle('report-locked', sceneOptions.reportLocked && !sceneOptions.reportDebug);
  hud.panel.hidden = false;
  hud.panel.innerHTML = `
    <button id="panelExpand" type="button" aria-expanded="false" aria-label="展开或收起报告"><span></span></button>
    ${questionMarkup(result.question, result.category, result.timeScope)}
    <div class="hexname">${p.name}</div>
    <div class="hexmeta">${p.palace}宫${p.rankLabel} · ${p.composition} · 世${p.shi} 应${p.ying}</div>
    ${result.changed ? `<div class="hexmeta">之 ${result.changed.name}</div>` : ''}
    <section class="report-preview" aria-label="卦象概览">
      ${!showFullReport ? `
        <div class="report-tier-label">免费排盘摘要</div>
        <div class="technical-teaser">
          ${preview.terms.map(({ label, value }) => `
            <div><span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong></div>`).join('')}
          <small>免费版仅展示排盘术语，不作结果判断。</small>
        </div>` : `
        <div class="direction-summary" data-level="${escapeHtml(preview.direction.level)}">
          <strong>卦象倾向 · ${escapeHtml(preview.direction.label)}</strong>
          <span>${escapeHtml(preview.direction.summary)}</span>
          <small>${escapeHtml(preview.direction.note)}</small>
        </div>`}
    </section>
    ${!showFullReport ? `
      <section class="report-lock" aria-label="完整报告已锁定">
        <strong>完整报告 · ¥3</strong>
        <span>付费后用大白话告诉你结果，并说明判断依据。</span>
        <ul>
          <li>一句话结果</li>
          <li>助力与阻力</li>
          <li>后续变化</li>
          <li>时间参考</li>
          <li>专业排盘依据</li>
        </ul>
        <button id="unlockReport" type="button" disabled>¥3 解锁完整报告</button>
        <small>支付通道正在接入，当前点击不会产生扣款。</small>
      </section>` : `
      <section class="report-detail" aria-label="完整证据报告">
        <div class="verdict-switch" role="group" aria-label="断语显示方式">
          <button id="verdictPlain" type="button" data-verdict-view="plain" aria-pressed="true">白话</button>
          <button id="verdictTechnical" type="button" data-verdict-view="technical" aria-pressed="false">术语</button>
          ${sceneOptions.reportDebug
            ? `<button id="verdictEvidence" type="button" data-verdict-view="evidence" aria-pressed="false">整理版</button>
              <button id="verdictRaw" type="button" data-verdict-view="raw" aria-pressed="false">原始字段</button>`
            : ''}
        </div>
        <div class="facts" id="verdictContent">
          <div class="verdict-view plain-report" data-verdict-pane="plain">
            ${plain.sections.map(({ title, text }) => `
              <article class="plain-report-section">
                <h3>${escapeHtml(title)}</h3>
                <p>${escapeHtml(text)}</p>
              </article>`).join('')}
            <p class="plain-report-note">${escapeHtml(PLAIN_VERDICT_NOTE)}</p>
          </div>
          <div class="verdict-view" data-verdict-pane="technical" hidden>
            ${report.technicalKeyPoints.map((text) => `<div>${escapeHtml(text)}</div>`).join('')}
          </div>
          ${sceneOptions.reportDebug ? `
            <div class="verdict-view report-evidence-draft" data-verdict-pane="evidence" hidden>
              ${reportEvidenceMarkup(evidenceDraft)}
            </div>
            <div class="verdict-view report-debug-fields" data-verdict-pane="raw" hidden>
              ${reportDebugMarkup(report, director.timing)}
            </div>` : ''}
        </div>
        <button id="copyPrompt" type="button">复制断卦提示词</button>
      </section>`}
  `;
  for (const button of hud.panel.querySelectorAll('[data-verdict-view]')) {
    button.onclick = (event) => {
      const before = panelSnapshot();
      event.preventDefault();
      event.stopPropagation();
      renderVerdictView(button.dataset.verdictView);
      // 两份内容常驻且外框固定；切换只改 hidden，内层滚动独立复位。
      hud.panel.scrollTop = 0;
      reportVerdictSwitch(event, button.dataset.verdictView, before);
    };
  }
  if (showFullReport) {
    renderVerdictView(sceneOptions.reportDebug ? 'evidence' : 'plain');
    document.getElementById('copyPrompt').onclick = () =>
      navigator.clipboard.writeText(toPrompt(result));
  }
  const panelExpand = document.getElementById('panelExpand');
  panelExpand.onclick = () => {
    const expanded = hud.panel.classList.toggle('mobile-expanded');
    panelExpand.setAttribute('aria-expanded', String(expanded));
    requestAnimationFrame(resize);
  };
}

function renderMeihuaResult(result) {
  hud.panel.classList.remove('result-panel');
  hud.panel.hidden = false;
  hud.panel.innerHTML = `
    <div class="hexname">${result.primary.name}</div>
    <div class="hexmeta">${result.method} · 上${result.upper}下${result.lower} · 动${result.movingLine}爻</div>
    <div class="hexmeta">体卦 ${result.ti}（${result.tiElement}） · 用卦 ${result.yong}（${result.yongElement}）</div>
    <div class="hexmeta">互卦 ${result.mutual.name} · 变卦 ${result.changed.name}</div>
    <div class="facts">${result.facts.map((fact) => `<div>${fact}</div>`).join('')}</div>`;
}

/* -------------------------------------------------------- 状态机 */

let appMode = 'liuyao';
let manualCastPending = false;

function closeMobileMenu() {
  modeUi.dock.classList.remove('mobile-open');
  document.documentElement.classList.remove('mobile-settings-open');
  mobileMenuButton.setAttribute('aria-expanded', 'false');
  requestAnimationFrame(resize);
}

function syncManualCastButton() {
  const state = director.state;
  const available = appMode === 'liuyao'
    && ![STATE.FORMED, STATE.TRANSFORM, STATE.COLLAPSE].includes(state);
  manualCastButton.hidden = !available;
  manualCastButton.disabled = state === STATE.EXPANDING || manualCastPending;
  if (state === STATE.CASTING) {
    manualCastButton.textContent = `手动摇卦 · 第${Math.min(6, director.values.length + 1)}爻`;
  } else if (state === STATE.EXPANDING || manualCastPending) {
    manualCastButton.textContent = '罗盘展开中…';
  } else {
    manualCastButton.textContent = '手动摇卦 · 第1爻';
  }
}

const director = new Director(field, {
  rand: Number.isFinite(measurementSeed) ? seededRandom(measurementSeed) : undefined,
  category: sceneOptions.category,
  question: sceneOptions.question,
  timeScope: sceneOptions.timeScope,
  onEvent: (name, payload) => {
    audio.handleDirectorEvent(name, payload);
    if (name === 'state') {
      document.documentElement.dataset.sceneState = payload.state;
      if (payload.state !== STATE.DORMANT) closeMobileMenu();
    }
    if (name === 'line') document.documentElement.dataset.lineCount = String(payload.index);
    if (name === 'formed') document.documentElement.dataset.sceneState = STATE.FORMED;
    if (name === 'transform') document.documentElement.dataset.sceneState = STATE.TRANSFORM;
    if (name === 'orientation-calibrated') {
      hud.calibration.hidden = !payload.active;
      document.documentElement.dataset.orientationCalibrated = String(Boolean(payload.active));
    }
    if (name === 'state' && appMode === 'liuyao') setHint(payload.state);
    if (name === 'line') renderLines();
    if (name === 'formed') {
      setHint(STATE.FORMED);
      renderResult(payload.result);
    }
    if (name === 'transform') setHint(STATE.TRANSFORM);
    if (name === 'state' || name === 'formed') syncCategoryControl();
    if (name === 'state' && payload.state === STATE.CASTING && manualCastPending) {
      manualCastPending = false;
      requestAnimationFrame(() => {
        if (director.state === STATE.CASTING) director.castLine(true);
        syncManualCastButton();
      });
    }
    if (name === 'state' || name === 'line' || name === 'formed' || name === 'transform') {
      syncManualCastButton();
    }
    if (name === 'state' || name === 'formed' || name === 'transform') {
      requestAnimationFrame(resize);
    }
  }
});

manualCastButton.onclick = () => {
  if (appMode !== 'liuyao') return;
  if (!director.result) {
    director.setQuestion(modeUi.question.value);
    director.setTimeScope(modeUi.timeScope.value);
    sceneOptions.question = director.question;
    sceneOptions.timeScope = director.timeScope;
  }
  if (director.state === STATE.CASTING) {
    director.castLine(true);
    syncManualCastButton();
    return;
  }
  if (director.state === STATE.EXPANDING) return;
  if (director.state === STATE.DORMANT) {
    manualCastPending = true;
    director.toAim();
    syncManualCastButton();
    setTimeout(() => {
      if (!manualCastPending || director.state !== STATE.AIM) return;
      director.toExpanding();
      syncManualCastButton();
    }, TIMING.awaken * 1000);
    return;
  }
  if (director.state === STATE.AIM) {
    manualCastPending = true;
    director.toExpanding();
    syncManualCastButton();
  }
};

const CATEGORY_OPTION_LABELS = Object.freeze({
  事业: '事业', 求财: '求财', 学业: '学业', 婚姻男测: '婚姻（男测）', 婚姻女测: '婚姻（女测）',
  疾病: '疾病', 出行: '出行', 子女: '子女', 官司: '官司', 房产: '房产', 自身: '自身'
});
const categoryOptions = Object.keys(CATEGORY_OPTION_LABELS)
  .filter((category) => Object.hasOwn(USE_GOD_MAP, category));
modeUi.category.innerHTML = [
  '<option value="">未选择（世爻）</option>',
  ...categoryOptions.map((category) => (
    `<option value="${escapeHtml(category)}">${escapeHtml(CATEGORY_OPTION_LABELS[category])}</option>`
  ))
].join('');
if (sceneOptions.category && !categoryOptions.includes(sceneOptions.category)) {
  modeUi.category.insertAdjacentHTML(
    'beforeend',
    `<option value="${escapeHtml(sceneOptions.category)}">${escapeHtml(sceneOptions.category)}</option>`
  );
}
modeUi.category.value = sceneOptions.category || '';
modeUi.question.value = sceneOptions.question || '';
modeUi.timeScope.value = sceneOptions.timeScope || 'none';

function syncCategoryControl() {
  const disabled = Boolean(director.result) || appMode !== 'liuyao';
  modeUi.category.disabled = disabled;
  modeUi.question.disabled = disabled;
  modeUi.timeScope.disabled = disabled;
  modeUi.category.value = director.category || '';
  modeUi.question.value = director.question || '';
  modeUi.timeScope.value = director.timeScope || 'none';
}

modeUi.category.onchange = () => {
  const nextCategory = modeUi.category.value || null;
  if (!director.setCategory(nextCategory)) {
    syncCategoryControl();
    return;
  }
  sceneOptions.category = nextCategory;
  renderPendingQuestion(director.question, director.category, director.timeScope);
  syncCategoryControl();
};
modeUi.question.onchange = () => {
  if (!director.setQuestion(modeUi.question.value)) {
    syncCategoryControl();
    return;
  }
  sceneOptions.question = director.question;
  renderPendingQuestion(director.question, director.category, director.timeScope);
  syncCategoryControl();
};
modeUi.question.addEventListener('blur', () => {
  // iOS 键盘收起后有时保留了临时视口偏移，将页面拉回原点。
  requestAnimationFrame(() => {
    globalThis.scrollTo(0, 0);
    resize();
  });
});
modeUi.timeScope.onchange = () => {
  if (!director.setTimeScope(modeUi.timeScope.value)) {
    syncCategoryControl();
    return;
  }
  sceneOptions.timeScope = director.timeScope;
  renderPendingQuestion(director.question, director.category, director.timeScope);
  syncCategoryControl();
};
setHint(STATE.DORMANT);
let measurementReferenceTilt = false;

function nowInBeijingForMeihua() {
  const now = new Date();
  const local = new Date(now.getTime() + (8 * 60 + now.getTimezoneOffset()) * 60000);
  return {
    year: local.getFullYear(),
    month: local.getMonth() + 1,
    day: local.getDate(),
    hour: local.getHours(),
    minute: local.getMinutes()
  };
}

const flyingStarAt = nowInBeijingForMeihua();
let flyingStarScale = 'year';

function setFlyingStarScale(scale = 'year') {
  flyingStarScale = scale === 'month' ? 'month' : 'year';
  const model = flyingStarRingModel(flyingStarAt, flyingStarScale);
  field.setFlyingStars(model);
  flyingStarUi.center.textContent = model.center.text;
  flyingStarUi.center.dataset.tone = model.center.tone;
  flyingStarUi.center.setAttribute('aria-label', `${model.scaleLabel}中宫 ${model.center.text}`);
  flyingStarUi.buttons.forEach((button) => {
    button.setAttribute('aria-pressed', String(button.dataset.feixingScale === flyingStarScale));
  });
  document.documentElement.dataset.feixingScale = flyingStarScale;
  window.__flyingStars = model;
}

flyingStarUi.buttons.forEach((button) => {
  button.onclick = () => setFlyingStarScale(button.dataset.feixingScale);
});
setFlyingStarScale('year');

function showMeihuaResult(result) {
  const highlightedLayers = field.highlightCells(cellMapFromMeihua(result));
  field.control.highlightReveal.fill(0);
  highlightedLayers.forEach((index) => { field.control.highlightReveal[index] = 1; });
  renderMeihuaResult(result);
  hud.title.textContent = '梅花易数 · 体用成卦';
  hud.sub.textContent = `${result.primary.name} → ${result.changed.name}`;
  audio.playFormedChord(0);
}

function setAppMode(mode) {
  appMode = mode === 'meihua' ? 'meihua' : 'liuyao';
  director.switchMode(appMode);
  field.setOrientation({});
  hud.lines.innerHTML = '';
  hud.panel.hidden = true;
  timingTooltip.root.hidden = true;
  modeUi.meihuaControls.hidden = appMode !== 'meihua';
  modeUi.buttons.forEach((button) => {
    button.setAttribute('aria-pressed', String(button.dataset.mode === appMode));
  });
  syncCategoryControl();
  if (appMode === 'meihua') {
    hud.title.textContent = '梅花易数';
    hud.sub.textContent = '按时间或数字起卦';
  } else {
    setHint(STATE.DORMANT);
    renderPendingQuestion(director.question, director.category, director.timeScope);
  }
  manualCastPending = false;
  closeMobileMenu();
  syncManualCastButton();
}

modeUi.buttons.forEach((button) => {
  button.onclick = () => setAppMode(button.dataset.mode);
});
modeUi.castTime.onclick = () => showMeihuaResult(castByTime(nowInBeijingForMeihua()));
modeUi.castNumbers.onclick = () => {
  const a = Number(modeUi.numberA.value);
  const b = Number(modeUi.numberB.value);
  if (!Number.isInteger(a) || a <= 0 || !Number.isInteger(b) || b <= 0) {
    hud.title.textContent = '请输入两个正整数';
    hud.sub.textContent = '前数为上卦，后数为下卦';
    return;
  }
  showMeihuaResult(castByNumbers(a, b, nowInBeijingForMeihua()));
};
setAppMode(sceneOptions.mode);
if (measurementMode && sceneOptions.measurePose !== 'rest') {
    field.setOrientation({
      y: (sceneOptions.measurePose === 'left' ? -1 : 1)
        * CAMERA.maxTiltY
        * CAMERA.measurePoseScale
    });
}

mobileMenuButton.onclick = () => {
  const open = modeUi.dock.classList.toggle('mobile-open');
  document.documentElement.classList.toggle('mobile-settings-open', open);
  mobileMenuButton.setAttribute('aria-expanded', String(open));
  requestAnimationFrame(resize);
};

let panelSwipeStartY = null;
hud.panel.addEventListener('pointerdown', (event) => {
  if (event.pointerType !== 'touch') return;
  panelSwipeStartY = event.clientY;
}, { passive: true });
hud.panel.addEventListener('pointerup', (event) => {
  if (event.pointerType !== 'touch' || panelSwipeStartY === null) return;
  const delta = event.clientY - panelSwipeStartY;
  panelSwipeStartY = null;
  const content = document.getElementById('verdictContent');
  if (delta < -60 && !hud.panel.classList.contains('mobile-expanded')) {
    hud.panel.classList.add('mobile-expanded');
  } else if (delta > 60 && hud.panel.classList.contains('mobile-expanded') && (content?.scrollTop || 0) === 0) {
    hud.panel.classList.remove('mobile-expanded');
  } else {
    return;
  }
  document.getElementById('panelExpand')?.setAttribute(
    'aria-expanded', String(hud.panel.classList.contains('mobile-expanded'))
  );
  requestAnimationFrame(resize);
}, { passive: true });

// 摇一摇只是点击的可选补充；未获授权时不影响主流程。
let lastShakeAt = 0;
let shakeEnabled = false;
shakeButton.onclick = async () => {
  if (shakeEnabled) {
    shakeEnabled = false;
  } else {
    const requestPermission = globalThis.DeviceMotionEvent?.requestPermission;
    if (typeof requestPermission === 'function') {
      shakeEnabled = (await requestPermission.call(globalThis.DeviceMotionEvent)) === 'granted';
    } else {
      shakeEnabled = true;
    }
  }
  shakeButton.setAttribute('aria-pressed', String(shakeEnabled));
  shakeButton.textContent = shakeEnabled ? '摇一摇已启用' : '启用摇一摇（可选）';
};
addEventListener('devicemotion', (event) => {
  if (!shakeEnabled || director.state !== STATE.CASTING || appMode !== 'liuyao') return;
  const a = event.accelerationIncludingGravity;
  if (!a) return;
  const force = Math.hypot(a.x || 0, a.y || 0, a.z || 0);
  const now = performance.now();
  if (force >= TOUCH.shakeThreshold && now - lastShakeAt >= TOUCH.shakeCooldownMs) {
    lastShakeAt = now;
    director.castLine(true);
  }
});

/* -------------------------------------------------------- 手势 */

const debouncer = new GestureDebouncer();
const orient = new Smoother(CAMERA.smoothing);
const aimFollow = new Smoother(AIM.smoothing);
const debugOrient = new Smoother(CAMERA.smoothing);
let palmDebugRaw = null;
let palmDebugSmooth = null;
let tracker = null;

function gestureControlBlockedByUi(activeElement = document.activeElement) {
  return isGestureControlBlocked({
    settingsOpen: modeUi.dock.classList.contains('mobile-open'),
    activeElement
  });
}

const HAND_CONNECTIONS = [
  [0, 1], [1, 2], [2, 3], [3, 4],
  [0, 5], [5, 6], [6, 7], [7, 8],
  [5, 9], [9, 10], [10, 11], [11, 12],
  [9, 13], [13, 14], [14, 15], [15, 16],
  [13, 17], [17, 18], [18, 19], [19, 20], [0, 17]
];
const gestureRecording = {
  active: false,
  startedAt: null,
  startedAtMs: 0,
  samples: []
};

function drawHandOverlay(landmarks) {
  if (!debugEnabled) return;
  const canvas = gestureDebug.overlay;
  const context = canvas.getContext('2d');
  const width = gestureDebug.video.videoWidth || DEBUG_UI.previewBufferWidth;
  const height = gestureDebug.video.videoHeight || Math.round(width / DEBUG_UI.previewAspect);
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }
  context.clearRect(0, 0, width, height);
  if (!landmarks) return;

  context.strokeStyle = new THREE.Color(COLORS.jade).getStyle();
  context.globalAlpha = DEBUG_UI.boneOpacity;
  context.lineWidth = DEBUG_UI.boneLineWidth;
  context.beginPath();
  for (const [from, to] of HAND_CONNECTIONS) {
    context.moveTo((1 - landmarks[from].x) * width, landmarks[from].y * height);
    context.lineTo((1 - landmarks[to].x) * width, landmarks[to].y * height);
  }
  context.stroke();

  context.fillStyle = new THREE.Color(COLORS.jadeBright).getStyle();
  context.globalAlpha = DEBUG_UI.landmarkOpacity;
  for (const landmark of landmarks) {
    context.beginPath();
    context.arc(
      (1 - landmark.x) * width,
      landmark.y * height,
      DEBUG_UI.landmarkRadius,
      0,
      Math.PI * 2
    );
    context.fill();
  }
  context.globalAlpha = 1;
}

function recordGestureFrame(landmarks) {
  if (!debugEnabled || !gestureRecording.active) return;
  const ratios = landmarks ? fingerRatios(landmarks) : null;
  gestureRecording.samples.push({
    elapsedMs: Number((performance.now() - gestureRecording.startedAtMs).toFixed(1)),
    ratios,
    classify: classify(landmarks)
  });
  gestureDebug.recordStatus.textContent = `${gestureRecording.samples.length} samples`;
}

function downloadGestureRecording() {
  const finishedAt = new Date();
  const payload = {
    schemaVersion: 1,
    startedAt: gestureRecording.startedAt,
    finishedAt: finishedAt.toISOString(),
    thresholds: {
      extendedRatio: GESTURE.extendedRatio,
      curledRatio: GESTURE.curledRatio,
      stableFrames: GESTURE.stableFrames,
      cooldown: GESTURE.cooldown,
      inferenceFps: GESTURE.inferenceFps
    },
    samples: gestureRecording.samples
  };
  const blob = new Blob([`${JSON.stringify(payload, null, 2)}\n`], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `gesture-calibration-${finishedAt.toISOString().replace(/[:.]/g, '-')}.json`;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

gestureDebug.record.onclick = () => {
  if (!gestureRecording.active) {
    gestureRecording.active = true;
    gestureRecording.startedAt = new Date().toISOString();
    gestureRecording.startedAtMs = performance.now();
    gestureRecording.samples = [];
    gestureDebug.record.textContent = '停止记录';
    gestureDebug.recordStatus.textContent = '0 samples';
    return;
  }
  gestureRecording.active = false;
  gestureDebug.record.textContent = '开始记录';
  downloadGestureRecording();
};

function renderGestureDebug(raw) {
  if (!debugEnabled) return;
  const names = ['thumb', 'index', 'middle', 'ring', 'pinky'];
  const ratios = tracker?.landmarks ? fingerRatios(tracker.landmarks) : null;
  if (tracker?.landmarks) {
    palmDebugRaw = palmOrientation(tracker.landmarks, {
      centerX: CAMERA.palmNeutralX,
      centerY: CAMERA.palmNeutralY
    });
    palmDebugSmooth = {
      ...debugOrient.push({
        x: palmDebugRaw.y,
        y: palmDebugRaw.x,
        z: palmDebugRaw.roll
      })
    };
  }
  gestureDebug.ratios.textContent = names
    .map((name) => `${name} ${ratios ? ratios[name].toFixed(3) : '—'}`)
    .join('  ');
  gestureDebug.classify.textContent = `classify ${raw ?? '—'}  confirmed ${debouncer.current ?? '—'}`;
  const since = Number.isFinite(debouncer._since) ? `${debouncer._since.toFixed(3)}s` : '∞s';
  gestureDebug.debounce.textContent = `count ${debouncer._count}  candidate ${debouncer._candidate ?? '—'}  since-confirm ${since}`;
  const wristY = tracker?.landmarks?.[0]?.y;
  const middleMcpY = tracker?.landmarks?.[9]?.y;
  gestureDebug.palmLandmarks.textContent = `wrist.y ${wristY?.toFixed(4) ?? '—'}  middleMCP.y ${middleMcpY?.toFixed(4) ?? '—'}`;
  gestureDebug.palmRaw.textContent = palmDebugRaw
    ? `palm cy ${palmDebugRaw.cy.toFixed(4)}  raw x ${palmDebugRaw.x.toFixed(4)}  y ${palmDebugRaw.y.toFixed(4)}  roll ${palmDebugRaw.roll.toFixed(4)}`
    : 'palm cy —  raw x —  y —  roll —';
  gestureDebug.palmSmooth.textContent = palmDebugSmooth
    ? `smooth x ${palmDebugSmooth.x.toFixed(4)}  y ${palmDebugSmooth.y.toFixed(4)}  roll ${palmDebugSmooth.z.toFixed(4)}`
    : 'smooth x —  y —  roll —';
  gestureDebug.rootRotation.textContent = `root x ${field.root.rotation.x.toFixed(4)}  y ${field.root.rotation.y.toFixed(4)}  z ${field.root.rotation.z.toFixed(4)}`;
  gestureDebug.panel.dataset.depthZ = JSON.stringify(field.layers.map(({ depthZ }) => depthZ));
  gestureDebug.panel.dataset.rootRotation = JSON.stringify([
    field.root.rotation.x,
    field.root.rotation.y,
    field.root.rotation.z
  ]);
  const previewRect = gestureDebug.preview.getBoundingClientRect();
  gestureDebug.panel.dataset.previewLayout = JSON.stringify({
    x: previewRect.x,
    y: previewRect.y,
    width: previewRect.width,
    height: previewRect.height,
    display: getComputedStyle(gestureDebug.preview).display,
    zIndex: getComputedStyle(gestureDebug.preview).zIndex
  });
}
renderGestureDebug(null);

let cameraStartPromise = null;
let cameraStartPhase = 'idle';
let cameraPhaseStartedAt = performance.now();

/**
 * The one camera + recognizer bootstrap used by every entry path. The manual
 * button and ?autocam=1 differ only in who asks for this function; neither is
 * allowed to open a video-only path. Concurrent requests share one promise so
 * a second tap cannot replace a tracker that is still compiling its WASM.
 */
function startCameraAndTracking(trigger = 'manual') {
  if (tracker?.ready) return Promise.resolve(tracker);
  if (cameraStartPromise) return cameraStartPromise;
  document.documentElement.dataset.cameraTrigger = trigger;
  cameraStartPromise = initializeCameraAndTracker(trigger)
    .finally(() => { cameraStartPromise = null; });
  return cameraStartPromise;
}

async function initializeCameraAndTracker(trigger) {
  const video = gestureDebug.video;
  const cameraStatusText = {
    'camera-requested': '◌ 请求摄像头权限',
    'camera-ready': '◌ 摄像头已开 · 模型加载中',
    'model-loading': '◌ 手势模型加载中',
    'vision-import-loading': '◌ 识别程序加载中',
    'vision-imported': '◌ 识别程序已加载',
    'wasm-loading': '◌ 识别引擎加载中',
    'wasm-ready': '◌ 识别引擎已就绪',
    'delegate-selected': '◌ 手势识别器初始化中',
    'landmarker-creating': '◌ 手势识别器初始化中',
    'gpu-timeout': '◌ GPU 初始化超时 · 切换 CPU',
    'delegate-fallback': '◌ 正在用 CPU 重建识别器',
    'tracker-ready': '● 手势识别就绪',
    error: '○ 手势模型加载失败'
  };
  tracker = new HandTracker({
    video,
    delegate: sceneOptions.handDelegate,
    onStatus: (status, detail = {}) => {
      cameraStartPhase = status;
      cameraPhaseStartedAt = performance.now();
      hud.cam.textContent = cameraStatusText[status] || status;
      document.documentElement.dataset.cameraStatus = status;
      document.documentElement.dataset.cameraTrigger = trigger;
      const loadingStatuses = new Set([
        'camera-requested', 'camera-ready', 'model-loading', 'vision-import-loading',
        'vision-imported', 'wasm-loading', 'wasm-ready', 'delegate-selected',
        'landmarker-creating'
      ]);
      if (loadingStatuses.has(status)) {
        hud.cameraNotice.hidden = false;
        hud.cameraNotice.dataset.kind = 'loading';
        hud.cameraNotice.textContent = '正在加载识别模型，首次约需 30 秒…';
      } else if (status === 'gpu-timeout' || status === 'delegate-fallback') {
        hud.cameraNotice.hidden = false;
        hud.cameraNotice.dataset.kind = 'fallback';
        hud.cameraNotice.textContent = 'GPU 初始化超时，正在切换 CPU 识别…';
      } else if (status === 'tracker-ready') {
        hud.cameraNotice.hidden = true;
      } else if (status === 'error') {
        hud.cameraNotice.hidden = false;
        hud.cameraNotice.dataset.kind = 'error';
        hud.cameraNotice.textContent = '手势识别加载失败，可重试或使用「手动摇卦」。';
      }
      if (detail.delegate) {
        document.documentElement.dataset.handDelegate = detail.delegate;
      }
      if (detail.error) {
        const message = String(detail.error?.stack || detail.error?.message || detail.error);
        document.documentElement.dataset.cameraError = message;
        globalThis.__reportCameraDiagnosticError?.(message);
      }
    },
    onFrame: (landmarks) => {
      drawHandOverlay(landmarks);
      recordGestureFrame(landmarks);
      if (!landmarks) return;
      // 诊断模式无论处于哪个状态，都显示当前手位若接管罗盘会产生的原始值
      // 与独立平滑值；只读不写 field，避免诊断本身改变状态或姿态。
      if (debugEnabled) {
        palmDebugRaw = palmOrientation(landmarks, {
          centerX: CAMERA.palmNeutralX,
          centerY: CAMERA.palmNeutralY
        });
        palmDebugSmooth = {
          ...debugOrient.push({
            x: palmDebugRaw.y,
            y: palmDebugRaw.x,
            z: palmDebugRaw.roll
          })
        };
      }
      // 用户设置问题时不让摄像头手势接管状态；否则输入中的手
      // 容易被误判为食指，触发 AIM 并连带收起设置抽屉。
      if (gestureControlBlockedByUi()) return;
      if (isAimFollowState(director.state)) {
        const visibleHeight = 2 * camera.position.z
          * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
        const target = fingertipAimPosition(landmarks, {
          width: visibleHeight * camera.aspect,
          height: visibleHeight
        });
        if (target) field.setAimPosition(aimFollow.push(target));
        return;
      }
      // 只有成卦后的掌心手势负责转向；AIM / CASTING 不再写 rotation。
      if (!isDepthOrientationState(director.state) || classify(landmarks) !== 'palm') return;
      // 真人自然抬手时掌心通常落在取景框下半部；以标定后的自然位为零，
      // 避免用户必须把手举到画面上沿才能让成卦恢复正中姿态。
      const o = palmOrientation(landmarks, {
        centerX: CAMERA.palmNeutralX,
        centerY: CAMERA.palmNeutralY
      });
      const s = orient.push({ x: o.y, y: o.x, z: o.roll });
      palmDebugRaw = { ...o };
      palmDebugSmooth = { ...s };
      field.setOrientation(palmRotationTarget(s, {
        maxTiltX: CAMERA.maxTiltX,
        maxTiltY: CAMERA.maxTiltY,
        maxRollZ: CAMERA.maxRollZ,
        pitchBias: CAMERA.palmPitchBias
      }));
    }
  });
  try {
    await tracker.start();
    video.dataset.live = 'true';
    hud.cam.textContent = `● 手势识别就绪 · ${tracker.delegate}`;
    // Camera readiness must not depend on WebAudio autoplay state. A sound
    // unlock failure used to fall into the camera catch block and discard a
    // perfectly healthy tracker.
    try {
      await unlockSound();
    } catch (soundError) {
      console.warn('[sound:unlock-failed]', soundError);
    }
    return tracker;
  } catch (e) {
    console.error('[camera:start-failed]', e);
    globalThis.__reportCameraDiagnosticError?.(e);
    delete video.dataset.live;
    hud.cam.textContent = '○ CAMERA OFF · 键盘操作 F/P/R';
    tracker = null;
    hud.cameraNotice.hidden = false;
    hud.cameraNotice.dataset.kind = 'error';
    hud.cameraNotice.textContent = '手势识别加载失败，可重试或使用「手动摇卦」。';
    return null;
  }
}
document.getElementById('startCam').onclick = () => {
  void startCameraAndTracking('manual');
};
if (sceneOptions.autocam) {
  document.documentElement.dataset.autocam = 'requested';
  void startCameraAndTracking('autocam').then(() => {
    document.documentElement.dataset.autocam = tracker?.ready ? 'live' : 'failed';
  });
}

canvas.addEventListener('pointermove', (event) => {
  const picked = field.pickInteractiveCell(
    event.clientX,
    event.clientY,
    camera,
    canvas.getBoundingClientRect()
  );
  if (!picked) {
    timingTooltip.root.hidden = true;
    return;
  }
  showTooltip(picked.tooltip, event.clientX, event.clientY);
});
canvas.addEventListener('pointerleave', () => { timingTooltip.root.hidden = true; });

function showTooltip(tooltip, clientX, clientY) {
  timingTooltip.title.textContent = tooltip.title;
  timingTooltip.body.textContent = [...tooltip.lines, tooltip.note].filter(Boolean).join('\n');
  timingTooltip.root.hidden = false;
  const rect = timingTooltip.root.getBoundingClientRect();
  const left = Math.min(
    clientX + TIMING_TOOLTIP.offsetX,
    innerWidth - rect.width - APP_UI.modeGap
  );
  const top = Math.min(
    clientY + TIMING_TOOLTIP.offsetY,
    innerHeight - rect.height - APP_UI.modeGap
  );
  timingTooltip.root.style.left = `${Math.max(APP_UI.modeGap, left)}px`;
  timingTooltip.root.style.top = `${Math.max(APP_UI.modeGap, top)}px`;
}

flyingStarUi.center.addEventListener('pointermove', (event) => {
  showTooltip(window.__flyingStars.center.tooltip, event.clientX, event.clientY);
});
flyingStarUi.center.addEventListener('pointerleave', () => { timingTooltip.root.hidden = true; });
flyingStarUi.center.addEventListener('focus', () => {
  const rect = flyingStarUi.center.getBoundingClientRect();
  showTooltip(window.__flyingStars.center.tooltip, rect.right, rect.bottom);
});
flyingStarUi.center.addEventListener('blur', () => { timingTooltip.root.hidden = true; });

// 键盘兜底，同时也是 Codex 做时序打点的入口
addEventListener('keydown', (e) => {
  // 中文输入法会发出 KeyI / Space 等按键，不能让它们落到
  // 唤醒、展开和摇卦快捷键上。设置抽屉打开时也统一停用快捷键。
  if (e.isComposing || gestureControlBlockedByUi(e.target)) return;
  if (e.code === 'KeyM') {
    audio.toggleMuted();
    syncSoundUi();
    if (!audio.muted) void unlockSound();
  }
  if (e.code === 'KeyY') {
    setFlyingStarScale(flyingStarScale === 'year' ? 'month' : 'year');
  }
  if (appMode === 'liuyao' && e.code === 'Space') {
    e.preventDefault();
    director.toExpanding();
  }
  if (appMode === 'liuyao' && e.code === 'KeyF') director.castLine(true);
  if (appMode === 'liuyao' && e.code === 'KeyP') director.toTransform();
  if (appMode === 'liuyao' && e.code === 'KeyI') director.onGesture('point');
  if (measurementMode && e.code === 'KeyC') director.toCollapse();
  if (measurementMode && e.code === 'KeyH') {
    const visible = field.control.highlightReveal.some((value) => value > 0);
    field.control.highlightReveal.fill(visible ? 0 : 1);
  }
  if (measurementMode && e.code === 'KeyT') {
    measurementReferenceTilt = !measurementReferenceTilt;
    field.setOrientation(measurementReferenceTilt ? {
      x: CAMERA.referenceTilt.x - CAMERA.restTilt.x,
      y: CAMERA.referenceTilt.y - CAMERA.restTilt.y,
      z: CAMERA.referenceTilt.z - CAMERA.restTilt.z
    } : {});
  }
  if (e.code === 'KeyR') {
    if (appMode === 'meihua') director.switchMode('meihua');
    else director.toDormant();
    hud.panel.hidden = true;
    hud.lines.innerHTML = '';
  }
});

/* --------------------------------------------------------- 主循环 */

const clock = new THREE.Clock();
let framebufferSampled = false;
const frameSample = {
  startedAt: performance.now(),
  previousAt: performance.now(),
  frames: 0,
  longestFrameMs: 0,
  reported: false
};

function applyPerformanceTier(tier, fps = null) {
  const normalized = Math.max(0, Math.min(2, Math.trunc(tier)));
  const textureSize = normalized === 0 ? FIELD.textureSizeMobile : 256;
  field.setPerformanceTier(normalized, {
    layerKeys: PERFORMANCE.tierTwoLayerKeys,
    textureSize: mobile ? textureSize : field.textureSize
  });
  if (normalized >= 1) {
    renderer.setPixelRatio(Math.min(
      devicePixelRatio,
      normalized === 1 ? PERFORMANCE.tierOnePixelRatio : PERFORMANCE.tierTwoPixelRatio
    ));
  }
  if (bloomPass) bloomPass.enabled = normalized < 2;
  document.documentElement.dataset.qualityTier = String(normalized);
  qualityBadge.dataset.visible = String(normalized > 0);
  qualityBadge.textContent = normalized > 0
    ? `已切换为流畅模式${normalized === 2 ? '·二' : ''}`
    : '完整画质';
  window.__quality = { tier: normalized, fps, textureSize: field.textureSize };
  resize();
}

qualityBadge.onclick = () => {
  applyPerformanceTier(0);
  qualityBadge.dataset.visible = 'false';
};

if (sceneOptions.qualityTier !== null) {
  applyPerformanceTier(sceneOptions.qualityTier);
  // 强制档是验收入口；贴图重建是一次性切档成本，不混入稳态 FPS。
  frameSample.startedAt = performance.now();
  frameSample.previousAt = frameSample.startedAt;
  frameSample.frames = 0;
  frameSample.longestFrameMs = 0;
}

function loop() {
  requestAnimationFrame(loop);
  const frameNow = performance.now();
  frameSample.frames += 1;
  frameSample.longestFrameMs = Math.max(
    frameSample.longestFrameMs,
    frameNow - frameSample.previousAt
  );
  frameSample.previousAt = frameNow;
  if (
    (measurementMode || mobile)
    && !frameSample.reported
    && frameNow - frameSample.startedAt >= PERFORMANCE.sampleDurationMs
  ) {
    const seconds = (frameNow - frameSample.startedAt) / PERFORMANCE.millisecondsPerSecond;
    const fps = frameSample.frames / seconds;
    console.info('[scene:fps]', JSON.stringify({
      mobile,
      frames: frameSample.frames,
      seconds,
      fps,
      longestFrameMs: frameSample.longestFrameMs
    }));
    frameSample.reported = true;
    if (mobile && sceneOptions.qualityTier === null) {
      applyPerformanceTier(
        fps < PERFORMANCE.tierTwoFps ? 2 : (fps < PERFORMANCE.tierOneFps ? 1 : 0),
        fps
      );
    }
  }
  const dt = Math.min(clock.getDelta(), PERFORMANCE.maxDeltaSeconds);
  if (cameraDiagnostics?.audio) {
    const diagnostic = audio.diagnostics;
    const unlock = firstSoundUnlockTrigger ? `yes(${firstSoundUnlockTrigger})` : 'no';
    const last = diagnostic.lastPlayback
      ? `${diagnostic.lastPlayback.at} ${diagnostic.lastPlayback.call} → ${diagnostic.lastPlayback.result}`
      : '—';
    cameraDiagnostics.audio.textContent = `audio ctx ${diagnostic.contextState} · unlock ${unlock} · wav ${diagnostic.decodedSamples}/${diagnostic.totalSamples} · last ${last}`;
    if (cameraDiagnostics.audioPulse) {
      const pulse = diagnostic.gesturePulse;
      const pulseResult = !pulse.attempted
        ? 'not-run'
        : (pulse.succeeded
          ? `started ${pulse.at} · confirm ${pulse.confirmation ? 'yes' : 'no'}`
          : `failed ${pulse.at} ${pulse.error || 'unknown'}`);
      cameraDiagnostics.audioPulse.textContent = `audio pulse ${pulseResult}`;
    }
  }
  if (tracker) {
    tracker.update(dt, performance.now());
    if (cameraDiagnostics) {
      cameraDiagnostics.video.textContent = `video ${gestureDebug.video.videoWidth}×${gestureDebug.video.videoHeight}`;
      cameraDiagnostics.fps.textContent = `detect ${tracker.detectCallsPerSecond.toFixed(1)}/s`;
      cameraDiagnostics.hands.textContent = `hands ${tracker.lastHandCount}`;
      const requested = tracker.requestedDelegate || sceneOptions.handDelegate;
      const delegateText = tracker.delegate || `— (requested ${requested})`;
      cameraDiagnostics.runtime.textContent = `delegate ${delegateText} · isolated ${String(globalThis.crossOriginIsolated === true)}`;
      const phaseSeconds = (performance.now() - cameraPhaseStartedAt) / 1000;
      cameraDiagnostics.phase.textContent = `phase ${cameraStartPhase} · ${phaseSeconds.toFixed(1)}s · ${document.documentElement.dataset.cameraTrigger || '—'}`;
    }
    const raw = classify(tracker.landmarks);
    // 没检测到手时不喂防抖器：null 本身不会触发 Director，也不会清掉
    // 当前已确认手势，避免短暂丢手后仍握着拳被当成一次新摇卦。
    if (tracker.landmarks && gestureControlBlockedByUi()) {
      debouncer.reset();
    } else if (tracker.landmarks) {
      const g = debouncer.feed(raw, dt);
      if (g && appMode === 'liuyao') director.onGesture(g);
    }
    renderGestureDebug(raw);
  }
  director.update(dt);
  updateCameraFraming();
  if (measurementMode) {
    const root = field.root;
    const html = document.documentElement;
    html.dataset.rootRotation = [root.rotation.x, root.rotation.y, root.rotation.z].join(',');
    html.dataset.rootPosition = root.position.toArray().join(',');
    html.dataset.sceneDepth = String(field.control.depth);
    html.dataset.spinMultiplier = String(field.control.spinMultiplier);
    html.dataset.ringRotations = field.layers.map(({ mesh }) => mesh.rotation.z).join(',');
    html.dataset.ringsSnapped = String(field.ringsSnapped);
    html.dataset.cameraProjection = [camera.position.z, camera.fov, camera.aspect].join(',');
  }
  composer.render();
  if (!framebufferSampled) {
    const gl = renderer.getContext();
    const pixel = new Uint8Array(4);
    gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, pixel);
    console.info('[scene:background-pixel]', JSON.stringify({
      rgba: [...pixel],
      hex: `#${[...pixel.slice(0, 3)].map((channel) => channel.toString(16).padStart(2, '0')).join('')}`
    }));
    framebufferSampled = true;
  }
}
loop();

// 给 Codex 调参用：控制台里可以直接改
window.__field = field;
window.__director = director;
window.__sceneDebug = { renderer, composer, camera };
window.__audio = audio;
logSceneDiagnostics();
const baseTextureCount = field.layers.length;
const staticHighlightTextureCount = field.layers.filter(
  ({ staticHighlightTexture }) => staticHighlightTexture
).length;
const pulseHighlightTextureCount = field.layers.filter(
  ({ pulseHighlightTexture }) => pulseHighlightTexture
).length;
const ringTextureBytes = (
  baseTextureCount * field.textureSize ** 2
  + (staticHighlightTextureCount + pulseHighlightTextureCount) * field.highlightTextureSize ** 2
) * 4;
console.info('[scene:resources]', JSON.stringify({
  mobile,
  ringLayers: field.layers.length,
  baseTextureSize: field.textureSize,
  highlightTextureSize: field.highlightTextureSize,
  baseTextureCount,
  staticHighlightTextureCount,
  pulseHighlightTextureCount,
  ringTextureMiBRaw: ringTextureBytes / (1024 ** 2),
  ringTextureMiBWithMipmaps: ringTextureBytes * (4 / 3) / (1024 ** 2),
  particles: field.particles.geometry.attributes.position.count,
  stars: field.stars.geometry.attributes.position.count
}));
